/**
 * What the Rename / Move dialog shows after a submission that did not simply finish: a refusal (conflict / source
 * changed / gone — with Check source again; a taken destination focuses the name field instead), "Outcome unknown" for an ambiguous answer, a PARTIAL move (a ready continuation token
 * remains — Continue move) and an UNCERTAIN move (a request was sent without a recorded answer — Check again; Continue
 * only when main's `canContinueMove` says so, never for an uncertain first request, r4 S8-R3-03).
 */
import { Button } from '../../../../../components/ui/button';
import type { HnsLocator, StorageOperationError } from '../../../../../types/azureStorage';
import { MOVE_AMBIGUOUS_TEXT, partialMoveText, uncertainMoveText } from './moveDialogModel';

export type MoveStatus =
  | { kind: 'idle' }
  | { kind: 'error'; error: StorageOperationError }
  | { kind: 'ambiguous' }
  | { kind: 'partial'; pendingMoveId: string; destination: HnsLocator }
  | { kind: 'uncertain'; pendingMoveId: string; destination: HnsLocator; canContinue: boolean };

type Props = {
  status: MoveStatus;
  source: HnsLocator;
  pending: boolean;
  onReload: () => void;
  onContinue: () => void;
  onRecheck: () => void;
};

/**
 * UI/UX fix round A (U37): "Check source again" re-reads a source that changed or went away; a taken destination
 * (`DestinationExists`) offers no reload — the dialog focuses the name field so another name can be typed.
 */
const reloadsSource = (error: StorageOperationError): boolean =>
  error.category === 'not_found' || (error.category === 'conflict' && error.code !== 'DestinationExists');

export function MoveStatusNotice({ status, source, pending, onReload, onContinue, onRecheck }: Props) {
  const busy = pending ? 'A request is in flight' : undefined;
  if (status.kind === 'idle') return null;
  if (status.kind === 'error') {
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm text-destructive" data-testid="storage-hns-move-error" data-code={status.error.code}>
        <span className="break-words">{status.error.message}</span>
        {reloadsSource(status.error) && <Button type="button" size="xs" variant="outline" disabled={pending} title={busy ?? `Read ${source.path || source.fileSystem} again`} onClick={onReload} data-testid="storage-hns-move-reload">Check source again</Button>}
      </div>
    );
  }
  if (status.kind === 'ambiguous') return <p className="text-sm text-warning" data-testid="storage-hns-move-ambiguous">{MOVE_AMBIGUOUS_TEXT}</p>;
  const partial = status.kind === 'partial';
  const canContinue = partial || status.canContinue;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-warning" data-testid="storage-hns-move-status" data-state={status.kind}>
      <span>{partial ? partialMoveText(source, status.destination) : uncertainMoveText(source, status.destination)}</span>
      {canContinue && <Button type="button" size="xs" disabled={pending} title={busy} onClick={onContinue} data-testid="storage-hns-move-continue">Continue move</Button>}
      {!partial && <Button type="button" size="xs" variant="outline" disabled={pending} title={busy} onClick={onRecheck} data-testid="storage-hns-move-recheck">Check again</Button>}
    </div>
  );
}
